import { ErrorMessage } from "@hookform/error-message";
import i18next from "i18next";
import debounce from "lodash.debounce";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdAdd, MdDelete, MdLabel } from "react-icons/md";
import Select, { components, MenuListProps } from "react-select";
import { toast } from "react-toastify";
import { useInfiniteTags } from "../api/hooks/useInfiniteTags";
import {
    useAllCollaborators,
    useCreateTag,
    useDeleteTag,
    usePagedTags,
    useUpdateAdminTags,
    useUpdateViewerTags,
} from "../api/hooks/useRiskThemes";
import { useDesignSystem } from "../context/designSystemContext";
import { customMultiSelectStyles } from "../styles/MultiSelectStyles";
import { Tag } from "../types/tag";
import { showErrorToast } from "../utils/errorToast";
import { mergeTagOptions, TagOption } from "../utils/tagSelectOptions";
import { ConfirmationModal } from "./ConfirmationModal";

interface CollaboratorWithTags {
  id: number;
  login: string;
  name?: string;
  email?: string;
  viewableTags?: { id: number; name: string }[];
  adminTags?: { id: number; name: string }[];
  viewableTagIds?: number[];
  adminTagIds?: number[];
}

export const ManageTags = () => {
  const { designSystem } = useDesignSystem();
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id: number | null;
    name: string;
  }>({ isOpen: false, id: null, name: "" });
  const [tagsSearch, setTagsSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [tagsListSearch, setTagsListSearch] = useState("");
  const PAGE_SIZE = 10;

  const { data: tagsPageResponse, isLoading: tagsLoading } = usePagedTags({
    page: currentPage,
    search: tagsListSearch,
    pageSize: PAGE_SIZE,
  });
  const tags = (tagsPageResponse?.content as Tag[] | undefined) ?? [];
  const totalPages: number = tagsPageResponse?.totalPages ?? 1;
  const isFirstPage: boolean = tagsPageResponse?.first ?? currentPage === 0;
  const isLastPage: boolean = tagsPageResponse?.last ?? true;
  const { data: collaborators, isLoading: collaboratorsLoading } =
    useAllCollaborators();
  const {
    tags: infiniteTags,
    isLoading: infiniteTagsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteTags({ enabled: true, search: tagsSearch, pageSize: 20 });

  const createTagMutation = useCreateTag();
  const deleteTagMutation = useDeleteTag();
  const updateViewerTagsMutation = useUpdateViewerTags();
  const updateAdminTagsMutation = useUpdateAdminTags();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
    },
  });

  const onSubmit = async (data: { name: string; description: string }) => {
    try {
      await createTagMutation.mutateAsync({
        name: data.name.trim(),
        description: data.description?.trim() || null,
      });
      toast.success(i18next.t("tag_create_success"));
      reset();
      setShowAddForm(false);
      setCurrentPage(0);
    } catch (error) {
      showErrorToast(error, i18next.t("tag_create_error"));
    }
  };

  const handleTagsListSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setTagsListSearch(value);
        setCurrentPage(0);
      }, 300),
    []
  );

  const handleDeleteClick = (id: number, name: string) => {
    setDeleteModal({ isOpen: true, id, name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.id) return;
    try {
      await deleteTagMutation.mutateAsync(deleteModal.id);
      toast.success(i18next.t("tag_delete_success"));
      setDeleteModal({ isOpen: false, id: null, name: "" });
      setCurrentPage(0);
    } catch (error) {
      showErrorToast(error, i18next.t("tag_delete_error"));
    }
  };

  const tagOptions = tags?.map((t: Tag) => ({ label: t.name, value: t.id })) ?? [];
  const baseTagOptions = useMemo(
    () => mergeTagOptions({ loadedTags: infiniteTags ?? [], selected: [] }),
    [infiniteTags]
  );

  const collaboratorsWithTags = (collaborators ?? []) as CollaboratorWithTags[];

  const getViewerValue = (collab: CollaboratorWithTags) => {
    if (collab.viewableTags?.length) {
      return collab.viewableTags.map((t) => ({ label: t.name, value: t.id }));
    }
    if (collab.viewableTagIds?.length && baseTagOptions.length) {
      return collab.viewableTagIds
        .map((id) => baseTagOptions.find((o) => o.value === id))
        .filter(Boolean) as TagOption[];
    }
    return [];
  };

  const getAdminValue = (collab: CollaboratorWithTags) => {
    if (collab.adminTags?.length) {
      return collab.adminTags.map((t) => ({ label: t.name, value: t.id }));
    }
    if (collab.adminTagIds?.length && baseTagOptions.length) {
      return collab.adminTagIds
        .map((id) => baseTagOptions.find((o) => o.value === id))
        .filter(Boolean) as TagOption[];
    }
    return [];
  };

  const handleViewerTagsChange = (collaboratorId: number, tagIds: number[]) => {
    updateViewerTagsMutation.mutate(
      { collaboratorId, tagIds },
      {
        onSuccess: () => toast.success(i18next.t("tag_permissions_updated")),
        onError: (error) => showErrorToast(error, i18next.t("tag_permissions_error")),
      }
    );
  };

  const handleAdminTagsChange = (collaboratorId: number, tagIds: number[]) => {
    updateAdminTagsMutation.mutate(
      { collaboratorId, tagIds },
      {
        onSuccess: () => toast.success(i18next.t("tag_permissions_updated")),
        onError: (error) => showErrorToast(error, i18next.t("tag_permissions_error")),
      }
    );
  };

  const fetchNextTagsPage = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const InfiniteMenuList = (props: MenuListProps<TagOption, true>) => {
    const { innerProps } = props;

    return (
      <components.MenuList
        {...props}
        innerProps={{
          ...innerProps,
          onScroll: (event) => {
            innerProps?.onScroll?.(event);

            const target = event.currentTarget;
            const reachedBottom =
              target.scrollHeight - target.scrollTop - target.clientHeight <= 2;

            if (reachedBottom) {
              fetchNextTagsPage();
            }
          },
        }}
      />
    );
  };

  return (
    <div className="space-y-8">
      {/* Seção: Cadastro de tags */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3
            className="font-openSans font-semibold text-lg"
            style={{ color: designSystem?.cssBasics.primaryColor }}
          >
            {i18next.t("tags_list_title")}
          </h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-openSans font-semibold text-white text-sm transition-opacity hover:opacity-90"
            style={{
              backgroundColor: designSystem?.cssBasics.secondaryColor,
            }}
          >
            <MdAdd className="w-5 h-5" />
            {i18next.t("tag_add_button")}
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder={i18next.t("tag_search_placeholder")}
            onChange={(e) => handleTagsListSearchChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-openSans"
          />
        </div>

        {showAddForm && (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mb-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label
                  className="block font-openSans text-sm font-medium mb-1"
                  style={{ color: designSystem?.cssBasics.primaryColor }}
                >
                  {i18next.t("tag_name")} *
                </label>
                <input
                  type="text"
                  {...register("name", {
                    required: i18next.t("required_field"),
                    minLength: {
                      value: 2,
                      message: i18next.t("min_3_characters_field"),
                    },
                  })}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={i18next.t("tag_name_placeholder")}
                />
                <ErrorMessage
                  errors={errors}
                  name="name"
                  render={({ message }) => (
                    <p className="text-red-500 text-xs mt-1">{message}</p>
                  )}
                />
              </div>
              <div>
                <label
                  className="block font-openSans text-sm font-medium mb-1"
                  style={{ color: designSystem?.cssBasics.primaryColor }}
                >
                  {i18next.t("tag_description")}
                </label>
                <input
                  type="text"
                  {...register("description")}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={i18next.t("tag_description_placeholder")}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    reset();
                    setShowAddForm(false);
                  }}
                  className="px-4 py-2 rounded-lg font-openSans font-semibold text-gray-700 text-sm border border-gray-300 hover:bg-gray-50"
                >
                  {i18next.t("button_cancel")}
                </button>
                <button
                  type="submit"
                  disabled={createTagMutation.isPending}
                  className="px-4 py-2 rounded-lg font-openSans font-semibold text-white text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: designSystem?.cssBasics.secondaryColor,
                  }}
                >
                  {createTagMutation.isPending
                    ? i18next.t("tag_creating")
                    : i18next.t("button_create")}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {tagsLoading ? (
            <div className="p-4 text-center text-gray-500">
              {i18next.t("loading_configurations")}
            </div>
          ) : tags && tags.length > 0 ? (
            <>
              <div className="divide-y divide-gray-200">
                {tags.map((tag: Tag) => (
                  <div
                    key={tag.id}
                    className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <MdLabel
                        className="w-5 h-5 flex-shrink-0 text-gray-500"
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <p className="font-openSans font-semibold text-gray-900 truncate">
                          {tag.name}
                        </p>
                        {tag.description && (
                          <p className="font-openSans text-sm text-gray-600 truncate">
                            {tag.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteClick(tag.id, tag.name)}
                      disabled={deleteTagMutation.isPending}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={i18next.t("button_delete")}
                    >
                      <MdDelete className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                  <span className="font-openSans text-sm text-gray-600">
                    {i18next.t("page_info", { current: currentPage + 1, total: totalPages })}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={isFirstPage}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-openSans disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {i18next.t("button_previous")}
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={isLastPage}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-openSans disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {i18next.t("button_next")}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-center text-gray-500">
              {i18next.t("tags_empty")}
            </div>
          )}
        </div>
      </div>

      {/* Seção: Permissões por colaborador (viewer / admin de tag) */}
      <div>
        <h3
          className="font-openSans font-semibold text-lg mb-2"
          style={{ color: designSystem?.cssBasics.primaryColor }}
        >
          {i18next.t("tags_permissions_title")}
        </h3>
        <p className="font-openSans text-gray-600 text-sm mb-4">
          {i18next.t("tags_permissions_description")}
        </p>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {collaboratorsLoading ? (
            <div className="p-4 text-center text-gray-500">
              {i18next.t("loading_configurations")}
            </div>
          ) : collaboratorsWithTags.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {collaboratorsWithTags.map((collab: CollaboratorWithTags) => (
                <div
                  key={collab.id}
                  className="p-4 space-y-3 hover:bg-gray-50/50 transition-colors"
                >
                  {(() => {
                    const viewerValue = getViewerValue(collab);
                    const adminValue = getAdminValue(collab);
                    const viewerOptions: TagOption[] = mergeTagOptions({
                      loadedTags: infiniteTags ?? [],
                      selected: viewerValue,
                    });
                    const adminOptions: TagOption[] = mergeTagOptions({
                      loadedTags: infiniteTags ?? [],
                      selected: adminValue,
                    });

                    return (
                      <>
                        <p className="font-openSans font-semibold text-gray-900">
                          {collab.name || collab.login}
                        </p>
                        <p className="font-openSans text-sm text-gray-600 -mt-1">
                          {collab.login}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                          <div>
                            <label className="block font-openSans text-xs font-medium text-gray-600 mb-1">
                              {i18next.t("tags_as_viewer")}
                            </label>
                            <Select
                              isMulti
                              options={viewerOptions}
                              value={viewerValue}
                              components={{ MenuList: InfiniteMenuList }}
                              onChange={(selected) =>
                                handleViewerTagsChange(
                                  collab.id,
                                  (selected ?? []).map((o) => o.value)
                                )
                              }
                              onInputChange={(value, meta) => {
                                if (meta.action === "input-change") {
                                  setTagsSearch(value);
                                }

                                return value;
                              }}
                              onMenuScrollToBottom={() => {
                                fetchNextTagsPage();
                              }}
                              styles={customMultiSelectStyles}
                              placeholder={i18next.t("tags_select_placeholder")}
                              classNamePrefix="react-select"
                              isLoading={
                                updateViewerTagsMutation.isPending ||
                                infiniteTagsLoading ||
                                isFetchingNextPage
                              }
                            />
                          </div>
                          <div>
                            <label className="block font-openSans text-xs font-medium text-gray-600 mb-1">
                              {i18next.t("tags_as_admin")}
                            </label>
                            <Select
                              isMulti
                              options={adminOptions}
                              value={adminValue}
                              components={{ MenuList: InfiniteMenuList }}
                              onChange={(selected) =>
                                handleAdminTagsChange(
                                  collab.id,
                                  (selected ?? []).map((o) => o.value)
                                )
                              }
                              onInputChange={(value, meta) => {
                                if (meta.action === "input-change") {
                                  setTagsSearch(value);
                                }

                                return value;
                              }}
                              onMenuScrollToBottom={() => {
                                fetchNextTagsPage();
                              }}
                              styles={customMultiSelectStyles}
                              placeholder={i18next.t("tags_select_placeholder")}
                              classNamePrefix="react-select"
                              isLoading={
                                updateAdminTagsMutation.isPending ||
                                infiniteTagsLoading ||
                                isFetchingNextPage
                              }
                            />
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">
              {i18next.t("collaborators_empty")}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, id: null, name: "" })
        }
        onConfirm={handleDeleteConfirm}
        title={i18next.t("tag_delete_modal_title")}
        message={i18next.t("tag_delete_confirmation", {
          name: deleteModal.name,
        })}
        confirmText={i18next.t("button_delete")}
        cancelText={i18next.t("button_cancel")}
        isLoading={deleteTagMutation.isPending}
      />
    </div>
  );
};
